import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Save, RotateCcw, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import { ReviewFollowupSettings } from "@/components/ReviewFollowupSettings";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

// Live preview merges. Mirrors what the backend produces in
// /api/invoices/{id}/send-review so what you see here is what gets sent.
function renderPreview(template, techNames) {
  let techCredit;
  if (techNames.length === 2) {
    techCredit = `Screen Techs (${techNames[0]} and ${techNames[1]})`;
  } else if (techNames.length === 1) {
    techCredit = `Screen Tech (${techNames[0]})`;
  } else {
    techCredit = "Screen Tech";
  }
  return (template || "")
    .replace(/\{tech_credit\}/g, techCredit)
    .replace(/\{review_url\}/g, "https://g.page/r/CTYpPySPKVV1EBE/review");
}

export default function ReviewTemplate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState("");
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [mergeTags, setMergeTags] = useState([]);
  // Preview controls — let the admin flip between 1 tech / 2 techs / no tech.
  const [previewMode, setPreviewMode] = useState("one"); // "one" | "two" | "none"

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await axios.get(`${API}/settings/review-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplate(res.data.template);
      setDefaultTemplate(res.data.default_template);
      setMergeTags(res.data.merge_tags || []);
    } catch (err) {
      toast.error("Failed to load review template");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template.trim()) {
      toast.error("Template body cannot be empty");
      return;
    }
    if (!template.includes("{review_url}")) {
      toast.error("Template must include the {review_url} merge tag");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      await axios.put(
        `${API}/settings/review-template`,
        { template },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Review template saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    if (!defaultTemplate) return;
    if (!window.confirm("Reset to the default template? Any unsaved changes will be lost.")) return;
    setTemplate(defaultTemplate);
    toast.message("Default template loaded — click Save to apply.");
  };

  const insertTag = (tag) => {
    setTemplate((prev) => `${prev}${prev.endsWith(" ") || prev.endsWith("\n") || !prev ? "" : " "}${tag}`);
  };

  const techNamesForPreview =
    previewMode === "two" ? ["Brett", "Jose"]
    : previewMode === "one" ? ["Brett"]
    : [];

  const previewBody = renderPreview(template, techNamesForPreview);
  const isUnchanged = template.trim() === defaultTemplate.trim();
  const charCount = previewBody.length;
  const smsSegments = charCount === 0 ? 0 : Math.ceil(charCount / 160);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-32">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Settings</span>
        </button>

        <div className="mb-6">
          <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase">
            Review Request Template
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            The SMS body sent to customers after an invoice is paid, when you click "Send Review Request".
          </p>
        </div>

        {/* Merge-tag helper bar */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
          <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Merge tags
          </h3>
          <p className="text-sm text-amber-900 mb-3">
            Tap a tag to insert it where your cursor was last typing. Tags get
            replaced with real values when the text is sent.
          </p>
          <div className="flex flex-wrap gap-2">
            {mergeTags.map((mt) => (
              <button
                key={mt.tag}
                type="button"
                onClick={() => insertTag(mt.tag)}
                className="text-xs font-mono bg-white border border-amber-300 text-amber-900 px-2 py-1 rounded-md hover:bg-amber-100"
                data-testid={`insert-tag-${mt.tag.replace(/[{}]/g, "")}`}
                title={mt.description}
              >
                {mt.tag}
              </button>
            ))}
          </div>
          <ul className="mt-3 space-y-1 text-xs text-amber-900/80">
            {mergeTags.map((mt) => (
              <li key={mt.tag}>
                <span className="font-mono">{mt.tag}</span> — {mt.description}
              </li>
            ))}
          </ul>
        </div>

        {/* Template editor */}
        <div className="bg-white rounded-xl border p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-zinc-900">Template body</h3>
            <button
              type="button"
              onClick={handleResetToDefault}
              disabled={isUnchanged}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="reset-review-template-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset to default
            </button>
          </div>
          <textarea
            data-testid="review-template-textarea"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            rows={14}
            className="w-full border border-zinc-200 rounded-lg p-3 text-sm text-zinc-900 font-mono focus:outline-none focus:ring-2 focus:ring-[#207024]/30 focus:border-[#207024] resize-y"
          />
          <p className="text-xs text-zinc-400 mt-2">
            {template.includes("{review_url}")
              ? "✓ Review link placeholder detected"
              : "⚠ Missing {review_url} — the link won't be included in the text"}
          </p>
        </div>

        {/* Live preview */}
        <div className="bg-white rounded-xl border p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-zinc-900">Live preview</h3>
            <div className="flex gap-1 bg-zinc-100 p-1 rounded-lg text-xs">
              {[
                { id: "one", label: "1 tech" },
                { id: "two", label: "2 techs" },
                { id: "none", label: "No tech" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setPreviewMode(opt.id)}
                  data-testid={`preview-mode-${opt.id}`}
                  className={`px-2 py-1 rounded-md font-medium transition ${
                    previewMode === opt.id
                      ? "bg-white text-zinc-900 shadow-sm"
                      : "text-zinc-500 hover:text-zinc-800"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div
            className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-700 whitespace-pre-wrap border"
            data-testid="review-template-preview"
          >
            {previewBody || (
              <span className="italic text-zinc-400">Empty — start typing above.</span>
            )}
          </div>
          <p className="text-xs text-zinc-400 mt-2">
            {charCount} characters • {smsSegments} SMS segment{smsSegments === 1 ? "" : "s"}
            {smsSegments > 1 && (
              <span className="text-amber-600"> (will send as a multi-part text)</span>
            )}
          </p>
        </div>

        {/* Save Button */}
        <button
          data-testid="save-review-template-btn"
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#207024] text-white py-3 rounded-xl font-semibold hover:bg-[#185a1c] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? "Saving..." : "Save Template"}
        </button>

        <ReviewFollowupSettings />
      </div>
    </div>
  );
}
